/**
 * Schema Management Service
 *
 * Handles API calls for structured data (schema.org) implementation
 * across blog posts and service pages.
 */

import { apiClient } from "./apiClient";
import type {
	SchemaRunResponse,
	RunStatusResponse,
	SchemaOverview,
	ApplyFAQSchemaRequest,
	ContentListingResponse,
	ContentFilters,
	SelectiveSchemaRequest,
	FAQContentType,
} from "../types/schema";

export const schemaService = {
	/**
	 * Get overview of schema implementation status
	 */
	async getOverview(tenantId: string): Promise<SchemaOverview> {
		const response = await apiClient.get<SchemaOverview>(
			"/api/schema/overview",
			{
				params: { tenant_id: tenantId },
			},
		);
		return response.data;
	},

	/**
	 * Apply Article schema to all published posts
	 */
	async applyArticleSchema(tenantId: string): Promise<SchemaRunResponse> {
		const response = await apiClient.post<SchemaRunResponse>(
			"/api/schema/article",
			{},
			{
				params: { tenant_id: tenantId },
			},
		);
		return response.data;
	},

	/**
	 * Apply FAQ schema to posts or service pages
	 */
	async applyFAQSchema(
		tenantId: string,
		contentType: ApplyFAQSchemaRequest["content_type"],
	): Promise<SchemaRunResponse> {
		const response = await apiClient.post<SchemaRunResponse>(
			"/api/schema/faq",
			{ content_type: contentType },
			{
				params: { tenant_id: tenantId },
			},
		);
		return response.data;
	},

	/**
	 * Run full schema implementation (Article + FAQ for posts and service pages)
	 */
	async runFullImplementation(tenantId: string): Promise<SchemaRunResponse[]> {
		const response = await apiClient.post<SchemaRunResponse[]>(
			"/api/schema/full",
			{},
			{
				params: { tenant_id: tenantId },
			},
		);
		return response.data;
	},

	/**
	 * Get status of a specific schema run
	 */
	async getRunStatus(runId: string): Promise<RunStatusResponse> {
		const response = await apiClient.get<RunStatusResponse>(
			`/api/schema/status/${runId}`,
		);
		return response.data;
	},

	/**
	 * Get content with schema status (for selection UI)
	 */
	async getContentWithSchemaStatus(
		tenantId: string,
		filters?: ContentFilters,
		page?: number,
		pageSize?: number,
	): Promise<ContentListingResponse> {
		const params: Record<string, string | number> = {
			tenant_id: tenantId,
		};

		if (filters?.content_type) {
			params.content_type = filters.content_type;
		}
		if (filters?.schema_status) {
			params.schema_status = filters.schema_status;
		}
		if (filters?.search) {
			params.search = filters.search;
		}
		if (page !== undefined) {
			params.page = page;
		}
		if (pageSize !== undefined) {
			params.page_size = pageSize;
		}

		const response = await apiClient.get<ContentListingResponse>(
			"/api/schema/content",
			{
				params,
			},
		);
		return response.data;
	},

	/**
	 * Apply article schema to selected items
	 */
	async applyArticleSchemaSelective(
		tenantId: string,
		request?: SelectiveSchemaRequest,
	): Promise<SchemaRunResponse> {
		const response = await apiClient.post<SchemaRunResponse>(
			"/api/schema/article",
			request || {},
			{
				params: { tenant_id: tenantId },
			},
		);
		return response.data;
	},

	/**
	 * Apply FAQ schema to selected items
	 */
	async applyFAQSchemaSelective(
		tenantId: string,
		contentType: FAQContentType,
		request?: SelectiveSchemaRequest,
	): Promise<SchemaRunResponse> {
		const response = await apiClient.post<SchemaRunResponse>(
			"/api/schema/faq",
			{
				content_type: contentType,
				...request,
			},
			{
				params: { tenant_id: tenantId },
			},
		);
		return response.data;
	},
};

export default schemaService;
